/**
 * Knack words marquee — seamless, gap-free loop.
 *
 * The phrase set is server-rendered once into .knack-marquee__track. A single set is usually
 * narrower than the screen, so a naive two-copy loop leaves a gap on the trailing edge when one
 * copy scrolls off (the next copy has not reached the edge yet). To avoid that we repeat the set
 * enough times that the track spans at least one viewport PLUS one set, then scroll by exactly one
 * set width (--marquee-distance). The copies line up, so there is never a seam and never a gap.
 *
 * The first set stays readable; the repeated sets are aria-hidden so the phrases are announced once.
 * No-ops under prefers-reduced-motion (CSS shows the single static set). Re-runs on
 * shopify:section:load and (debounced) on resize so the fill stays correct as the viewport changes.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const build = (viewport) => {
    const track = viewport.querySelector('[data-knack-marquee-track]');
    if (!track) return;

    // Remember the original single set so every rebuild starts from a clean slate.
    if (track.dataset.knackBase == null) {
      track.dataset.knackBase = track.innerHTML;
    }
    track.innerHTML = track.dataset.knackBase;
    track.style.removeProperty('--marquee-distance');

    // Reduced motion (or nothing to show): leave the single static set, no animation.
    if (REDUCED.matches || !track.children.length) return;

    // Measure one set against the viewport.
    const setWidth = track.scrollWidth;
    const viewWidth = viewport.clientWidth;
    if (setWidth === 0 || viewWidth === 0) return;

    // Repeat until the track is at least one screen + one set wide, so it never empties mid-loop.
    const copies = Math.ceil(viewWidth / setWidth) + 1;
    const base = track.dataset.knackBase;
    for (let i = 1; i < copies; i += 1) {
      const holder = document.createElement('template');
      holder.innerHTML = base;
      // The repeats are decorative duplicates; hide them from assistive tech.
      Array.from(holder.content.children).forEach((child) => child.setAttribute('aria-hidden', 'true'));
      track.appendChild(holder.content);
    }

    // Loop by exactly one set width — copy N+1 lands where copy N started, so the seam is invisible.
    track.style.setProperty('--marquee-distance', `${setWidth}px`);
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-knack-marquee]').forEach(build);
  };

  init();

  // Re-measure once webfonts have loaded, since phrase widths change with the final font.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => init());
  }

  // Theme editor: rebuild when this (or any) section re-renders.
  document.addEventListener('shopify:section:load', (event) => init(event.target || document));

  // Re-fill if the viewport width changes (rotation / resize), debounced.
  let resizeTimer;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => init(), 200);
    },
    { passive: true }
  );

  // Rebuild if the motion preference changes at runtime.
  const onMotionChange = () => init();
  if (REDUCED.addEventListener) {
    REDUCED.addEventListener('change', onMotionChange);
  } else if (REDUCED.addListener) {
    REDUCED.addListener(onMotionChange);
  }
})();
